import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';

// Create a configured axios instance
const api = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: true
});

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add request interceptor to add token
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle token refresh
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          const refreshToken = localStorage.getItem('refresh_token');
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }
          
          const response = await api.post('/api/auth/refresh', {
            refresh_token: refreshToken
          });
          
          if (response.data.status === 'success') {
            localStorage.setItem('access_token', response.data.access_token);
            api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          await logout();
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );

  const register = async (registrationData) => {
    try {
      const response = await api.post('/api/auth/register', registrationData);
      
      if (response.data.status === 'success') {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setCurrentUser(response.data.user);
      }
      
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const login = async (loginData) => {
    try {
      console.log('Attempting login with data:', loginData);
      const response = await api.post('/api/auth/verify-login', loginData);
      console.log('Login response:', response.data);
      
      if (response.data.status === 'success') {
        console.log('Login successful, storing data...');
        const { access_token, refresh_token, user } = response.data;
        
        // Store tokens and user data
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Set axios default authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Update current user state
        console.log('Setting current user:', user);
        setCurrentUser(user);
        
        return response.data;
      }
      
      return response.data;
    } catch (error) {
      console.error('Login error details:', error.response?.data);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      setCurrentUser(null);
    }
  };

  const verifyToken = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');
      
      if (!token || !storedUser) {
        setCurrentUser(null);
        return;
      }

      // Set axios default authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.post('/api/auth/verify-token');
      if (response.data.status === 'success') {
        setCurrentUser(JSON.parse(storedUser));
      } else {
        setCurrentUser(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      setCurrentUser(null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
    }
  };

  useEffect(() => {
    verifyToken().finally(() => setLoading(false));
  }, []);

  const value = {
    currentUser,
    setCurrentUser,
    register,
    login,
    logout,
    loading,
    error,
    user: currentUser,
    api // Export the configured axios instance
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 